import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useNotify } from '../../../hooks';
import { useApiClient } from '../../../hooks/useRestAPIHooks';
import { useReportErrorToBugsnag } from '../../../util/bugsnagReporting';
import { Folder, ModelUtils } from '../models';
import {
  getTemplatingItemsInFolder,
  getTemplatingItemSummariesForPrefix,
} from './getTemplatingItemsInFolder';
import { useInvalidateTemplatingItems } from './useInvalidateTemplatingItems';

const messages = defineMessages({
  folderRenamed: {
    id: 'useRenameTemplatingFolder.folderRenamed',
    defaultMessage: 'Folder renamed',
  },
  folderRenameFailed: {
    id: 'useRenameTemplatingFolder.folderRenameFailed',
    defaultMessage: 'Failed to rename folder',
  },
  folderNameAlreadyExists: {
    id: 'useRenameTemplatingFolder.folderNameAlreadyExists',
    defaultMessage: 'A folder with that name already exists',
  },
});

export const useRenameTemplatingFolder = () => {
  const apiClient = useApiClient();
  const [notify] = useNotify();
  const reportError = useReportErrorToBugsnag();
  const invalidateTemplatingItems = useInvalidateTemplatingItems();
  const { formatMessage } = useIntl();
  const [isRenaming, setIsRenaming] = useState(false);

  const renameTemplatingFolder = useCallback(
    async (folder: Folder, newName: string) => {
      const renamedFolder = ModelUtils.withName(folder, newName);
      if (ModelUtils.hasSamePath(folder, renamedFolder)) {
        return true;
      }

      setIsRenaming(true);
      try {
        const parentFolder = ModelUtils.getParentFolder(folder);
        const siblings = await getTemplatingItemSummariesForPrefix(
          apiClient,
          parentFolder.path,
        );
        const conflictingFolder = siblings
          .filter(item => item.type === 'folder')
          .find(
            item =>
              ModelUtils.hasSamePath(
                ModelUtils.getParentFolder(item),
                parentFolder,
              ) &&
              ModelUtils.getName(item) === newName &&
              !ModelUtils.hasSamePath(item, folder),
          );

        if (conflictingFolder) {
          notify(formatMessage(messages.folderNameAlreadyExists), 'error');
          return false;
        }

        const templatingItems = await getTemplatingItemsInFolder(
          apiClient,
          folder,
        );
        for (const templatingItem of templatingItems) {
          await apiClient.request('PATCH /v1/ai-templates/:id', {
            type: templatingItem.type,
            id: templatingItem.id,
            path: ModelUtils.reparent(templatingItem, folder, renamedFolder)
              .path,
          });
        }

        invalidateTemplatingItems(folder, renamedFolder, parentFolder);
        notify(formatMessage(messages.folderRenamed), 'success');
        return true;
      } catch (error) {
        reportError(error);
        notify(formatMessage(messages.folderRenameFailed), 'error');
        return false;
      } finally {
        setIsRenaming(false);
      }
    },
    [apiClient, formatMessage, invalidateTemplatingItems, notify, reportError],
  );

  return {
    renameTemplatingFolder,
    isRenaming,
  };
};
